import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Chip,
    IconButton,
    TextField,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    useTheme,
    useMediaQuery,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    Alert,
    Card,
    CardContent,
    CardActions,
    Tooltip,
    Skeleton
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import RateReviewIcon from '@mui/icons-material/RateReview';
import apiClient from '../api';
import { useAuth } from '../context/AuthContext';

function BoardCardSkeletons() {
    return (
        <Stack spacing={1}>
            {[0, 1, 2].map((i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                    <Skeleton variant="text" width="68%" height={22} />
                    <Skeleton variant="text" width="46%" height={16} />
                    <Skeleton variant="text" width="54%" height={16} />
                </Paper>
            ))}
        </Stack>
    );
}

function QuoteManagementPage() {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [error, setError] = useState(null);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [activeId, setActiveId] = useState(null);

    // Kanban columns
    const columns = [
        { id: 'new', title: 'New Requests', color: '#1976d2', icon: <RequestQuoteIcon /> },
        { id: 'in_review', title: 'In Review', color: '#f57c00', icon: <RateReviewIcon /> },
        { id: 'quoted', title: 'Quoted', color: '#7b1fa2', icon: <AssessmentIcon /> },
        { id: 'won', title: 'Won', color: '#2e7d32', icon: <EmojiEventsIcon /> },
        { id: 'lost', title: 'Lost', color: '#d32f2f', icon: <CancelIcon /> }
    ];


    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get('/intake-admin/quotes', {
                params: { page: 1, limit: 1000, search }
            });
            const data = res.data.data || { items: [] };
            setQuotes(data.items);
        } catch (err) {
            console.error('Error fetching quotes:', err);
            setError('Failed to load quote requests. Please try again.');
        } finally {
            setLoading(false);
            setReady(true);
        }
    };

    useEffect(() => {
        fetchData();
    }, [search]);

    const markViewed = async (id) => {
        try {
            await apiClient.post(`/intake-admin/quotes/${id}/viewed`);
            fetchData();
        } catch (err) {
            console.error('Error marking as viewed:', err);
        }
    };

    const handleUpdate = async (id, updates) => {
        try {
            await apiClient.patch(`/intake-admin/quotes/${id}`, updates);
            fetchData();
            if (detailItem && detailItem._id === id) {
                setDetailItem({ ...detailItem, ...updates });
            }
        } catch (err) {
            console.error('Error updating quote:', err);
            setError('Failed to update quote request.');
        }
    };

    const handleCardClick = (item) => {
        setDetailItem(item);
        setDetailOpen(true);
        markViewed(item._id);
    };

    const handleDeleteRequest = (item, e) => {
        e.stopPropagation();
        setItemToDelete(item);
        setConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        try {
            await apiClient.delete(`/intake-admin/quotes/${itemToDelete._id}`);
            setConfirmOpen(false);
            setItemToDelete(null);
            fetchData();
        } catch (err) {
            console.error('Error deleting quote:', err);
            setError('Failed to delete quote request.');
        }
    };

    const getItemsByStatus = (status) => {
        return quotes.filter(q => q.status === status);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        console.log('🚀 DRAG START:', {
            activeId: event.active.id,
            activeData: event.active.data.current,
            activeRect: event.active.rect.current.translated,
            columns: columns.map(c => c.id)
        });
    };

    const handleDragMove = (event) => {
        console.log('🔄 DRAG MOVE:', {
            activeId: event.active.id,
            overId: event.over?.id,
            overData: event.over?.data.current,
            delta: event.delta,
            activeRect: event.active.rect.current.translated
        });
    };

    const handleDragEnd = async (event) => {
        setActiveId(null);
        console.log('🏁 DRAG END:', {
            activeId: event.active.id,
            overId: event.over?.id,
            overData: event.over?.data.current,
            activeRect: event.active.rect.current.translated,
            overRect: event.over?.rect,
            collision: event.collisions
        });

        if (!event.over) {
            console.log('❌ No drop target - drag cancelled');
            return;
        }

        const quoteId = event.active.id;
        const quote = quotes.find(q => q._id === quoteId);
        if (!quote) {
            console.log('❌ Quote not found:', quoteId);
            return;
        }

        let newStatus = null;

        // Check if we're dropping onto a column by looking at the over.id
        const isColumn = columns.find(c => c.id === event.over.id);
        if (isColumn) {
            newStatus = event.over.id;
            console.log('✅ Dropping onto column:', newStatus);
        } else {
            // We're dropping onto another card, find which column it belongs to
            const targetQuote = quotes.find(q => q._id === event.over.id);
            if (targetQuote) {
                newStatus = targetQuote.status;
                console.log('✅ Dropping onto card in column:', newStatus);
            } else {
                console.log('❌ Could not determine target column');
            }
        }

        if (!newStatus || quote.status === newStatus) {
            console.log('⏭️ No status change needed:', { current: quote.status, new: newStatus });
            return;
        }

        console.log(`🔄 Moving quote ${quoteId} from ${quote.status} to ${newStatus}`);

        try {
            // Update the quote status
            await handleUpdate(quoteId, { status: newStatus });
            console.log(`✅ Quote ${quoteId} moved to ${newStatus}`);
        } catch (err) {
            console.error('❌ Error updating quote status:', err);
            setError('Failed to update quote status.');
        }
    };

    const renderStatsCards = () => {
        const stats = {
            total: quotes.length,
            new: quotes.filter(q => q.status === 'new').length,
            quoted: quotes.filter(q => q.status === 'quoted').length,
            won: quotes.filter(q => q.status === 'won').length,
            lost: quotes.filter(q => q.status === 'lost').length
        };

        const winRate = stats.won + stats.lost > 0 ? Math.round((stats.won / (stats.won + stats.lost)) * 100) : 0;
        const statNumber = (value) => (ready ? (
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
        ) : (
            <Skeleton variant="text" width={56} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.35)' }} />
        ));

        return (
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" spacing={3}>
                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', height: '100%' }}>
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>Total Requests</Typography>
                                        {statNumber(stats.total)}
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                        <AssessmentIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', height: '100%' }}>
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>New Requests</Typography>
                                        {statNumber(stats.new)}
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                        <RequestQuoteIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', color: 'white', height: '100%' }}>
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>Won Deals</Typography>
                                        {statNumber(stats.won)}
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                        <EmojiEventsIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', height: '100%' }}>
                            <Box sx={{ p: { xs: 2, md: 3 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="overline" sx={{ fontWeight: 600, opacity: 0.9 }}>Win Rate</Typography>
                                        {statNumber(`${winRate}%`)}
                                    </Box>
                                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}>
                                        <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                </Stack>
            </Box>
        );
    };

    const SortableQuoteCard = ({ item }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ 
            id: item._id,
            data: {
                type: 'quote',
                status: item.status,
                item: item
            }
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0 : 1, // Hide the original when dragging
            cursor: 'grab',
            marginBottom: 8,
            zIndex: isDragging ? 1000 : 'auto',
            position: 'relative'
        };

        console.log(`📦 SortableQuoteCard ${item._id}:`, {
            isDragging,
            transform,
            status: item.status,
            style
        });

        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
                {renderQuoteCard(item)}
            </div>
        );
    };

    const DroppableColumn = ({ column, items }) => {
        const { setNodeRef, isOver } = useDroppable({
            id: column.id,
            data: {
                type: 'column',
                status: column.id
            }
        });

        console.log(`🏗️ DroppableColumn ${column.id}:`, {
            isOver,
            itemsCount: items.length
        });

        return (
            <Paper
                key={column.id}
                sx={{
                    minWidth: { xs: 220, sm: 240 },
                    maxWidth: { xs: 220, sm: 260 },
                    flex: '1 0 auto',
                    borderTop: '2px solid',
                    borderTopColor: column.color,
                    borderRadius: 1.5,
                    backgroundColor: isOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    border: isOver ? '2px dashed #1976d2' : '2px solid transparent'
                }}
            >
                <Box sx={{ px: 1.25, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                        <Avatar sx={{ bgcolor: column.color, width: 26, height: 26, '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                            {column.icon}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                            {column.title}
                        </Typography>
                    </Stack>
                    {ready ? (
                        <Chip label={`${items.length}`} title={`${items.length} request${items.length !== 1 ? 's' : ''}`} size="small" sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} />
                    ) : (
                        <Skeleton variant="rounded" width={28} height={22} />
                    )}
                </Box>

                <SortableContext 
                    items={items.map(item => item._id)} 
                    strategy={verticalListSortingStrategy} 
                    id={column.id}
                >
                    <Box 
                        ref={setNodeRef}
                        sx={{ 
                            p: 1, 
                            minHeight: 280, 
                            maxHeight: 'min(58vh, 520px)', 
                            overflowY: 'auto',
                            position: 'relative',
                            border: '2px solid transparent',
                            '&:hover': {
                                border: '2px dashed #1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                        data-droppable-id={column.id}
                        data-column-id={column.id}
                        data-column-title={column.title}
                    >
                        {!ready ? (
                            <BoardCardSkeletons />
                        ) : items.length === 0 ? (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                    minHeight: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Drop here
                                </Typography>
                            </Box>
                        ) : (
                            items.map(item => <SortableQuoteCard key={item._id} item={item} />)
                        )}
                    </Box>
                </SortableContext>
            </Paper>
        );
    };

    const renderQuoteCard = (item) => (
        <Card
            key={item._id}
            sx={{
                mb: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: !item.viewedAt ? '2px solid #1976d2' : '1px solid #e0e0e0',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 3
                }
            }}
            onClick={() => handleCardClick(item)}
        >
            <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {!item.viewedAt && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'info.main', flexShrink: 0 }} />
                        )}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap title={`${item.requesterFirstName || ''} ${item.requesterLastName || ''}`.trim()}>
                            {item.requesterFirstName} {item.requesterLastName}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={(e) => handleDeleteRequest(item, e)} sx={{ color: 'error.main', p: 0.25 }} aria-label="Delete">
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Stack>

                <Stack spacing={0.35}>
                    {item.sourcePayload?.rqCompany && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <BusinessIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap>{item.sourcePayload.rqCompany}</Typography>
                        </Box>
                    )}
                    {item.truckType && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <LocalShippingIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap>{item.truckType}</Typography>
                        </Box>
                    )}
                    {item.requesterEmail && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <EmailIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap title={item.requesterEmail}>{item.requesterEmail}</Typography>
                        </Box>
                    )}
                    {item.requesterPhone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary">{item.requesterPhone}</Typography>
                        </Box>
                    )}
                    {(item.origin || item.destination) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <PlaceIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap title={`${item.origin || 'N/A'} → ${item.destination || 'N/A'}`}>
                                {item.origin || 'N/A'} → {item.destination || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    {(item.pickupDate || item.deliveryDate) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EventIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {item.pickupDate ? new Date(item.pickupDate).toLocaleDateString() : 'N/A'} → {item.deliveryDate ? new Date(item.deliveryDate).toLocaleDateString() : 'N/A'}
                            </Typography>
                        </Box>
                    )}
                </Stack>

                {item.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic', overflow: 'hidden' }}>
                        Note: {item.notes}
                    </Typography>
                )}
            </CardContent>
            <CardActions sx={{ pt: 0, px: 1.25, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {new Date(item.createdAt).toLocaleDateString()}
                </Typography>
            </CardActions>
        </Card>
    );

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {renderStatsCards()}

            {/* Controls */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Sales Pipeline
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
                            Refresh
                        </Button>
                        <Button
                            size="small"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            variant={showFilters ? "contained" : "outlined"}
                        >
                            Filters
                        </Button>
                    </Stack>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by requester, company, origin, destination..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Stack>
                </Collapse>
            </Paper>

            {/* Kanban Board */}
            <DndContext 
                sensors={sensors} 
                collisionDetection={closestCenter} 
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd}
            >
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5, mx: -0.5, px: 0.5 }}>
                    {columns.map((column) => {
                        const items = getItemsByStatus(column.id);
                        return (
                            <DroppableColumn 
                                key={column.id}
                                column={column} 
                                items={items} 
                            />
                        );
                    })}
                </Box>
                
                <DragOverlay>
                    {activeId ? (
                        <Box sx={{ 
                            transform: 'rotate(5deg)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            {renderQuoteCard(quotes.find(q => q._id === activeId))}
                        </Box>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Quote Request Details</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers data-usage-screen="quotes.detail">
                    {detailItem && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Submitted</Typography>
                                <Typography variant="body1">{new Date(detailItem.createdAt).toLocaleString()}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Website</Typography>
                                <Typography variant="body1">{detailItem.websiteDomain || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">First Name</Typography>
                                <TextField size="small" fullWidth value={detailItem.requesterFirstName || ''} onChange={(e) => setDetailItem({ ...detailItem, requesterFirstName: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { requesterFirstName: e.target.value })} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Last Name</Typography>
                                <TextField size="small" fullWidth value={detailItem.requesterLastName || ''} onChange={(e) => setDetailItem({ ...detailItem, requesterLastName: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { requesterLastName: e.target.value })} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                <TextField size="small" fullWidth value={detailItem.requesterEmail || ''} onChange={(e) => setDetailItem({ ...detailItem, requesterEmail: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { requesterEmail: e.target.value })} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                <TextField size="small" fullWidth value={detailItem.requesterPhone || ''} onChange={(e) => setDetailItem({ ...detailItem, requesterPhone: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { requesterPhone: e.target.value })} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Company</Typography>
                                <Typography variant="body1">{detailItem.sourcePayload?.rqCompany || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Truck Type</Typography>
                                <TextField size="small" fullWidth value={detailItem.truckType || ''} onChange={(e) => setDetailItem({ ...detailItem, truckType: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { truckType: e.target.value })} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Pickup Date</Typography>
                                <TextField size="small" fullWidth type="date" value={detailItem.pickupDate ? detailItem.pickupDate.substring(0, 10) : ''} onChange={(e) => setDetailItem({ ...detailItem, pickupDate: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { pickupDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Delivery Date</Typography>
                                <TextField size="small" fullWidth type="date" value={detailItem.deliveryDate ? detailItem.deliveryDate.substring(0, 10) : ''} onChange={(e) => setDetailItem({ ...detailItem, deliveryDate: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { deliveryDate: e.target.value })} InputLabelProps={{ shrink: true }} />
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">Origin</Typography>
                                <TextField size="small" fullWidth value={detailItem.origin || ''} onChange={(e) => setDetailItem({ ...detailItem, origin: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { origin: e.target.value })} />
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">Destination</Typography>
                                <TextField size="small" fullWidth value={detailItem.destination || ''} onChange={(e) => setDetailItem({ ...detailItem, destination: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { destination: e.target.value })} />
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">Details</Typography>
                                <TextField size="small" fullWidth multiline rows={3} value={detailItem.details || ''} onChange={(e) => setDetailItem({ ...detailItem, details: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { details: e.target.value })} />
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                    <Select
                                        value={detailItem.status || 'new'}
                                        onChange={(e) => {
                                            const newStatus = e.target.value;
                                            handleUpdate(detailItem._id, { status: newStatus });
                                        }}
                                    >
                                        <MenuItem value="new">New</MenuItem>
                                        <MenuItem value="in_review">In Review</MenuItem>
                                        <MenuItem value="quoted">Quoted</MenuItem>
                                        <MenuItem value="won">Won</MenuItem>
                                        <MenuItem value="lost">Lost</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography variant="caption" color="text.secondary">Internal Notes</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={detailItem.notes || ''}
                                    onChange={(e) => setDetailItem({ ...detailItem, notes: e.target.value })}
                                    onBlur={(e) => handleUpdate(detailItem._id, { notes: e.target.value })}
                                    placeholder="Add internal notes..."
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Delete Quote Request?
                </DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this quote request? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default QuoteManagementPage;
